import {
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
} from '@angular/forms';

@Component({
	selector: 'app-file-dropped',
	templateUrl: './file-dropped.component.html',
	styleUrls: ['./file-dropped.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FileDroppedComponent),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => FileDroppedComponent),
			multi: true,
		},
	],
})
export class FileDroppedComponent
	implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
	@Input() multiple: boolean = false;
	@Input() height: string = '110px';
	@Input() styles: string = '';
	@Input() title: string = 'Upload your files';
	@Input() accept: string =
		'image/png, image/jpeg, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf';
	@Input() isEditMode: boolean = false;
	@Input() isError: boolean = false;
	@Input() errorMessage: string = 'El archivo es requerido';
	@Input() maxFileSize: number = 500000; // 500 KB
	@Output() filesSelected = new EventEmitter<any[]>();
	@Output() deleteFileIndex = new EventEmitter<number | null>();
	@ViewChild('fileDropRef', { static: false })
	fileDropRef!: ElementRef<HTMLInputElement>;
	allowedExtensions = /(\.jpeg|\.jpg|\.png|\.docx|\.xlsx|\.pdf|\.zip)$/i;

	files: any[] = [];
	validFiles: boolean = true;
	validSizes: boolean = true;
	disabled: boolean = false;

	private onChange: (files: any[]) => void = () => {};
	private onTouched: () => void = () => {};

	ngOnDestroy(): void {
		this.files = [];
		this.deleteFileIndex.emit(null);
		this.onChange(this.files);
		this.resetForm();
	}

	ngOnInit(): void {
		this.files = [];
		this.onChange(this.files);
		this.resetForm();
	}

	writeValue(files: any[]): void {
		if (Array.isArray(files)) {
			this.files = files;
		} else {
			this.files = [];
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = (files: any[]) => {
			fn(Array.isArray(files) ? files : []);
		};
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (this.fileDropRef) {
			this.fileDropRef.nativeElement.disabled = this.disabled;
		}
	}

	validate(_control: AbstractControl): ValidationErrors | null {
		if (this.isEditMode) return null;
		return this.files.length > 0 ? null : { required: true };
	}

	onFileDropped($event: any) {
		if (this.disabled) return;
		if (!this.multiple) {
			this.files = [];
		}
		this.prepareFilesList($event);
	}

	fileBrowseHandler($event: Event) {
		if (this.disabled) return;
		const files = (<HTMLInputElement>$event.target).files;
		if (files) {
			if (!this.multiple) {
				this.files = [];
			}
			this.prepareFilesList(Array.from(files));
		}
	}

	deleteFile(index: number) {
		if (this.disabled) return;

		this.deleteFileIndex.emit(index);
		this.files.splice(index, 1);
		this.onChange(this.files);
		if (this.fileDropRef) {
			this.fileDropRef.nativeElement.value = '';
		}
	}

	uploadFilesSimulator(index: number) {
		setTimeout(() => {
			if (!Array.isArray(this.files) || index >= this.files.length) {
				return;
			} else {
				const progressInterval = setInterval(() => {
					if (this.files[index]?.progress === 100) {
						clearInterval(progressInterval);
						this.uploadFilesSimulator(index + 1);
					} else if (this.files[index]) {
						this.files[index].progress += 5;
					}
				}, 200);
			}
		}, 1000);
	}

	prepareFilesList(files: any[]) {
		this.validFiles = true;
		this.validSizes = true;

		const allowedExtensions = this.accept.split(',').map((ext) => ext.trim());

		for (const item of files) {
			const extension = item.type;
			const fileSize = item.size / 100;

			if (!allowedExtensions.includes(extension)) {
				this.validFiles = false;
				continue;
			}

			if (fileSize > this.maxFileSize) {
				this.validSizes = false;
				continue;
			}

			item.progress = 0;
			this.files.push(item);
		}

		this.filesSelected.emit(this.files);
		this.uploadFilesSimulator(0);
		this.onChange(this.files);
	}

	formatBytes(bytes: number, decimals: number = 2): string {
		if (bytes === 0) {
			return '0 Bytes';
		}
		const k = 1024;
		const dm = decimals <= 0 ? 0 : decimals;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
	}

	resetForm(): void {
		this.files = [];
		if (this.fileDropRef) {
			this.fileDropRef.nativeElement.value = '';
		}
	}
}
